import { Box, Container, Typography, Stack, useTheme } from '@mui/material'
import { KFooter } from 'components/core/KFooter'

export const PrivacyPolicyPage = () => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Box
          sx={{
            minHeight: '100%',
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h1" gutterBottom>
              PRIVACY POLICY
            </Typography>

            <Stack spacing={3} sx={{ mt: 4 }}>
              <Box>
                <Typography variant="h3" gutterBottom>
                  Kiolyn is a part of ChaChaPOS Inc.
                </Typography>
                <Typography paragraph>
                  Protecting your privacy is very important to Kiolyn. We
                  designed this Privacy Policy to make clear to you how we may
                  collect, use and share your personal information. "Personal
                  information" means any information that can be used to
                  personally identify you (this does not include any information
                  that has been aggregated or made anonymous). Please read this
                  policy carefully in order to understand what we do with your
                  information.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  The Information Kiolyn Collects About You
                </Typography>
                <Typography paragraph>
                  <strong>Kiolyn Affiliated Merchant:</strong> If you are an
                  affiliate merchant, we may collect the names, business
                  addresses, email addresses and other contact information of
                  your employees and officers to the extent necessary for the
                  provision to you of the Kiolyn services.
                </Typography>
                <Typography paragraph>
                  <strong>User Provided Information:</strong> If you are a
                  customer of a Kiolyn affiliated merchant, when you sign up
                  with a Kiolyn affiliated merchant, the merchant will collect
                  the personal information you volunteer, for example, your
                  name, your address and your email address. This information
                  will be passed to us to store within the Kiolyn system. When
                  your payment to the merchant is processed, we only receive the
                  last 4 digits of your payment credit card number or payment
                  identification number. Your credit card number is otherwise
                  encrypted by the swipe hardware used by the Kiolyn merchant
                  and processed automatically. Kiolyn stores the information we
                  receive about you in the portion of our database associated
                  with the Kiolyn affiliated merchant that collected it.
                </Typography>
                <Typography paragraph>
                  <strong>Purchase Transactions:</strong> When you make
                  purchases with other merchants that use Kiolyn, Kiolyn will
                  keep records of your transaction history and some information
                  about your computer or the device you use to interoperate with
                  the Kiolyn merchants that you frequent.
                </Typography>
                <Typography paragraph>
                  <strong>Automatically Collected Information:</strong> We may
                  automatically record certain information from your system by
                  using different types of tracking technology. This
                  "automatically collected" information may include Internet
                  Protocol address ("IP Address"), a unique device or user ID,
                  version of software installed, system type, the dates, times
                  and details of your transactions, and information from your
                  account that you make publicly available.
                </Typography>
              </Box>

              <Box>
                <Typography paragraph>
                  <strong>Cookies Information:</strong>
                  In order to enhance the services we can offer you, we may send
                  one or more cookies – small text files that contain a string
                  of alphanumeric characters – to your computer. Kiolyn may use
                  both session cookies and persistent cookies. A session cookie
                  disappears after you close your browser. A persistent cookie
                  remains after you close your browser and may be used by your
                  browser on subsequent visits to merchants that use the Kiolyn
                  platform.
                </Typography>
                <Typography paragraph>
                  Persistent cookies can be removed by adjusting the settings on
                  your web browser. Please review your web browser's "Help" file
                  to learn the proper way to modify your cookie settings. You
                  should understand that some features of a merchant's Kiolyn
                  account associated with you may not function properly if you
                  don't accept cookies. For further information on cookies,
                  including information on how to remove them, visit
                  www.aboutcookies.org or www.allaboutcookies.org.
                </Typography>
                <Typography paragraph>
                  At Kiolyn we do not sell, rent, lease or otherwise disclose
                  your email address or other personally identifiable
                  information or personally identifiable customer data to
                  others, and we will not make your personal information
                  available to any unaffiliated parties, except our approved
                  agents or resellers, partners and contractors, or as otherwise
                  described in this Privacy Policy. This section explains how we
                  use your personal information.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  The Way Kiolyn Uses Your Personal Information
                </Typography>
                <Typography paragraph>
                  <strong>Quality Control and Routine Maintenance:</strong>{' '}
                  Kiolyn uses the information that you provide or that we
                  collect to operate, maintain, enhance, and provide all of the
                  Kiolyn features and services to Kiolyn affiliated merchants.
                  We may share your information with our trusted third party
                  affiliates and partners, to ensure that you have a safe,
                  high-quality experience when using Kiolyn affiliated
                  merchants. We rely on some of our third party affiliates and
                  partners for support of the products and services we offer.
                  All such third party affiliates and partners are required to
                  preserve the confidentiality of any personal information they
                  may access.
                </Typography>
                <Typography paragraph>
                  We may use certain information about you without identifying
                  you as an individual to third parties. We do this for purposes
                  such as analyzing how the Kiolyn service is used, marketed and
                  improved, diagnosing service or technical problems,
                  maintaining security, and personalizing content.
                </Typography>
                <Typography paragraph>
                  <strong>Administrative Contact:</strong> We will use your
                  email address, without your further consent, for
                  administrative communications to you. If you wish to change
                  your communication settings, you may do so by accessing your
                  Kiolyn account and making the modifications directly.
                </Typography>
                <Typography paragraph>
                  <strong>Marketing:</strong> We may use your personal
                  information and transaction history to send you information
                  about services offered by Kiolyn-affiliated merchants or to
                  market sponsored events, products or services that we believe
                  may be of interest to you. We also have an email newsletter
                  that we distribute to all of our customers. You may opt out of
                  these communications at any time via a link within the email.
                </Typography>
                <Typography paragraph>
                  Your transaction history and location may be used as
                  aggregated, non-personal information to generate general
                  statistics about purchasing behavior of a population that you
                  are a part of, in order to provide better services. We may use
                  or distribute this aggregated data without your consent. This
                  data cannot be readily reversed engineered to derive your
                  individual personal data.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  Remarketing
                </Typography>
                <Typography paragraph>
                  Third-party vendors, namely Google (via Google Analytics), may
                  place cookies on your computer to track certain views of key
                  pages on Kiolyn's websites. This information is used to then
                  display banners and other media advertising to you at a later
                  time. We will never sell, rent or otherwise distribute this
                  information to any other party outside of advertising networks
                  we use in order to remarket to you. Users may opt-out of
                  Google's use of cookies by visiting the Google Ads Preferences
                  Manager.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  Legal Compliance
                </Typography>
                <Typography paragraph>
                  We may also disclose information where we are required to do
                  so by law, for example, in response to a court order or a
                  subpoena. To the extent permitted by applicable law, we also
                  may disclose information in response to a law enforcement
                  agency's or other public agency's request or if we feel that
                  such disclosure may prevent the instigation of a crime,
                  facilitate an investigation related to public safety, to
                  protect the security or integrity of our websites, or enable
                  us to take precautions against liability.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  Voluntarily Provided Information
                </Typography>
                <Typography paragraph>
                  When making a purchase from Kiolyn for certain products, for
                  example, hardware or supplies, your contact information will
                  be shared with the fulfillment vendor that ships the products
                  to you. You understand that these fulfillment vendors may use
                  or export your contact information once you provide it to
                  Kiolyn for the purposes of fulfilling your order.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  Merger or Sale of Kiolyn
                </Typography>
                <Typography paragraph>
                  In the event that Kiolyn is acquired by or merged with a
                  third-party entity, you hereby consent to transfer or assign
                  the information that we have collected from you as part of
                  that merger, acquisition, sale or other change of control, to
                  the extent permitted by applicable law.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  Data Security
                </Typography>
                <Typography paragraph>
                  We maintain your personal data in a secure server environment.
                  The data is encrypted in transit, using tier 1 cloud service
                  providers. Your entire credit card number will not be recorded
                  in the clear by Kiolyn, however the last the four digits of
                  your credit card number may be stored and used to track your
                  transaction history.
                </Typography>
                <Typography paragraph>
                  We maintain your personal data in a secure server environment.
                  We have implemented certain appropriate security measures to
                  help protect your personal information from accidental loss
                  and from unauthorized access, use, or disclosure. For example,
                  we use Secure Sockets Layer (SSL) technology to connect the
                  point sale device to the Kiolyn system. Also, we store the
                  information about you in a data center with restricted access,
                  and we use a variety of technical security measures to secure
                  your data. However, please note that we cannot guarantee that
                  unauthorized persons will always be unable to defeat our
                  security measures.
                </Typography>
                <Typography paragraph>
                  Also, please note that we may store and process your personal
                  information in systems located outside of your home country.
                  However, regardless of where storage and processing may occur,
                  we take appropriate steps to ensure that your information is
                  protected, consistent with the principles set forth under this
                  Policy.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  International Visitors
                </Typography>
                <Typography paragraph>
                  The following provisions apply to users outside the United
                  States:
                </Typography>
                <Typography component="ul" sx={{ pl: 4 }}>
                  <Typography component="li">
                    You consent to having your personal data transferred to and
                    processed in the United States and/or re-exported outside
                    the United States. If your personal data is collected within
                    the European Union, you consent to your data being
                    transferred out of the European Economic Area to Kiolyn.
                  </Typography>
                  <Typography component="li">
                    If you are located in a country embargoed by the United
                    States, or are on the U.S. Treasury Department's list of
                    Specially Designated Nationals you will not use Kiolyn to
                    engage in commercial activities, such as receiving payments.
                  </Typography>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h3" gutterBottom>
                  How to Contact Kiolyn
                </Typography>
                <Typography paragraph>
                  Please contact us if you have any questions about our privacy
                  policy or information we hold about you. We have an internal
                  privacy policy ombudsman, who can be contacted with any
                  complaints or comments at privacy@Kiolyn.com.
                </Typography>
                <Typography paragraph>
                  Alternatively, please feel free to write to us at (in the US)
                  Kiolyn. 21800 Hesperian Blvd, Hayward, CA 94541.
                </Typography>
              </Box>

              <Box>
                <Typography paragraph>
                  <strong>DISCLAIMER:</strong> KIOLYN MAKES NOT REPRESENTATION
                  OR WARRANTIES, EXPRESS OR IMPLIED WITH RESPECT TO ANY PRODUCTS
                  OR SERVICES PAID FOR USING THE KIOLYN PLATFORM OR PURCHASED
                  FROM KIOLYN AFFILIATED MERCHANTS.
                </Typography>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>

      <KFooter />
    </Box>
  )
}
